import { getDb } from '../db';
import type { Phrase } from '../../core/types';

const byOrder = (a: Phrase, b: Phrase): number =>
  a.pinned === b.pinned ? a.order - b.order : Number(b.pinned) - Number(a.pinned);

/**
 * `PhraseRepo` per `specs/10-contracts.md`.
 *
 * Order is explicit and manual in Phase 1. Usage-driven reordering arrives with
 * learning in Phase 2 and only on an explicit "Neu sortieren" — a board that
 * rearranges itself mid-conversation defeats motor memory, which here is worth more
 * than optimal ranking (§5.4).
 */
export const phraseRepo = {
  async all(): Promise<Phrase[]> {
    const db = await getDb();
    return (await db.getAll('phrases')).sort(byOrder);
  },

  async byCategory(categoryId: string): Promise<Phrase[]> {
    const db = await getDb();
    return (await db.getAllFromIndex('phrases', 'byCategory', categoryId)).sort(byOrder);
  },

  async get(id: string): Promise<Phrase | undefined> {
    const db = await getDb();
    return db.get('phrases', id);
  },

  async upsert(phrase: Phrase): Promise<void> {
    const db = await getDb();
    await db.put('phrases', phrase);
  },

  /** Destructive → PIN-gated at the surface, never here. */
  async remove(id: string): Promise<void> {
    const db = await getDb();
    const existing = await db.get('phrases', id);
    if (existing?.locked) {
      // The Notfall core set is protected (1.10). Failing loudly is correct: this is
      // a programming error, not something a caregiver should be able to reach.
      throw new Error(`refusing to delete locked phrase ${id}`);
    }
    await db.delete('phrases', id);
  },

  async recordUse(id: string): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('phrases', 'readwrite');
    const existing = await tx.store.get(id);
    if (existing) {
      await tx.store.put({
        ...existing,
        useCount: existing.useCount + 1,
        lastUsedAt: new Date().toISOString(),
      });
    }
    await tx.done;
  },

  async setAudioKey(id: string, audioKey: string): Promise<void> {
    const db = await getDb();
    const tx = db.transaction('phrases', 'readwrite');
    const existing = await tx.store.get(id);
    if (existing) await tx.store.put({ ...existing, audioKey });
    await tx.done;
  },
};
